import React, { useState, useEffect, useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { FaSearch, FaSlidersH, FaLayerGroup } from 'react-icons/fa';
import ProductCard from '../common/ProductCard';
import { productsData, productCategories } from '../../data/productsData';
import { API_CONFIG } from '../../config/api';
import './Products.css';

const Products = () => {
  const [products, setProducts] = useState(productsData);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [loading, setLoading] = useState(false);

  // Attempt to fetch from API on mount; fallback gracefully to productsData
  useEffect(() => {
    setLoading(true);
    fetch(`${API_CONFIG.BASE_URL}/products.php`)
      .then(res => {
        if (!res.ok) throw new Error('API server unavailable');
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const formatted = data.map((apiItem, index) => {
            const fallbackItem = productsData.find(p => 
              p.name.toLowerCase() === apiItem.name.toLowerCase() || p.id === apiItem.id
            ) || productsData[index % productsData.length];

            return {
              ...fallbackItem,
              ...apiItem,
              images: apiItem.images && apiItem.images.length > 0 
                ? apiItem.images.map(img => ({
                    ...img,
                    image_path: API_CONFIG.getImageUrl(img.image_path)
                  }))
                : fallbackItem.images
            };
          });
          setProducts(formatted);
        }
        setLoading(false);
      })
      .catch(() => {
        // Use curated fallback productsData
        setProducts(productsData);
        setLoading(false);
      });
  }, []);

  // Filter & Sort Logic
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Category Filter
    if (selectedCategory !== 'all') {
      result = result.filter(p => {
        const cat = (p.category || '').toLowerCase();
        const name = (p.name || '').toLowerCase();
        if (selectedCategory === 'flagship') return cat.includes('flagship') || name.startsWith('i ');
        if (selectedCategory === 'nova') return cat.includes('nova') || name.includes('nova');
        if (selectedCategory === 'sc') return cat.includes('sc') || name.includes('sc');
        if (selectedCategory === 'forme') return cat.includes('forme') || name.includes('forme');
        return true;
      });
    }

    // Search Query Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(q) ||
        (p.description && p.description.toLowerCase().includes(q)) ||
        (p.specs && p.specs.toLowerCase().includes(q))
      );
    }

    // Sorting Logic
    if (sortBy === 'name-asc') {
      result.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'name-desc') {
      result.sort((a, b) => b.name.localeCompare(a.name));
    } else {
      result.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    }

    return result;
  }, [products, selectedCategory, searchQuery, sortBy]);

  return (
    <div className="products-page-root">
      {/* Catalog Hero Banner */}
      <section className="catalog-hero-section">
        <Container>
          <div className="catalog-hero-content text-center">
            <span className="catalog-eyebrow">Smartphone Lineup</span>
            <h1 className="catalog-main-title">
              Designed for performance.<br />Crafted with precision.
            </h1>
            <p className="catalog-subtitle">
              Explore our mobile models, features, specifications, and authorized service centers across the country.
            </p>
          </div>
        </Container>
      </section>

      {/* Filter and Category Navigation Toolbar */}
      <section className="catalog-toolbar-section">
        <Container>
          {/* Series Category Tabs */}
          <div className="category-tabs-scroll-wrapper">
            <div className="category-tabs-row" role="tablist">
              {productCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`category-tab-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                  role="tab"
                  aria-selected={selectedCategory === cat.id}
                >
                  <span className="tab-name">{cat.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Search, Sort, and Results Meta Bar */}
          <div className="catalog-control-bar">
            {/* Search Input */}
            <div className="catalog-search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search models, features, or specs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="catalog-search-input"
                aria-label="Search catalog"
              />
              {searchQuery && (
                <button 
                  className="search-clear-btn" 
                  onClick={() => setSearchQuery('')}
                >
                  &times;
                </button>
              )}
            </div>

            {/* Results Count & Sort Dropdown */}
            <div className="catalog-sort-group">
              <div className="catalog-count-badge">
                <FaLayerGroup className="count-icon" />
                <span>Showing <strong>{filteredProducts.length}</strong> {filteredProducts.length === 1 ? 'model' : 'models'}</span>
              </div>

              <div className="catalog-sort-wrapper">
                <FaSlidersH className="sort-icon" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="catalog-sort-select"
                  aria-label="Sort products"
                >
                  <option value="default">Sort: Lineup Order</option>
                  <option value="name-asc">Model Name (A to Z)</option>
                  <option value="name-desc">Model Name (Z to A)</option>
                </select>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Main Product Catalog Grid */}
      <section className="catalog-grid-section">
        <Container>
          {loading ? (
            <div className="catalog-loading-state">
              <div className="catalog-spinner" />
              <p>Loading smartphone lineup...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="catalog-empty-state">
              <h3>No devices found</h3>
              <p>No smartphone models match your search "{searchQuery}". Try resetting filters.</p>
              <button 
                className="apple-btn apple-btn-secondary"
                onClick={() => {
                  setSelectedCategory('all');
                  setSearchQuery('');
                  setSortBy('default');
                }}
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="catalog-products-grid">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </Container>
      </section>
    </div>
  );
};

export default Products;
